import React from "react"
import { useStore } from "../../../teafuljs/store"
import Card from "@material-ui/core/Card"
import CardHeader from "@material-ui/core/CardHeader"
import CardContent from "@material-ui/core/CardContent"
import { Doughnut, HorizontalBar } from "react-chartjs-2"
import SegmentRefs from "../../../data/SegmentTitles.json"
// import SegmentQueriesArray from "../../../data/SegmentQueriesArray.json"
import OTNTRefData from "../../../data/completeRefList.json"
import bibleBookList from "../../../data/BookChapterList.json"
import { getTranslation } from "../../../scripts/getTranslation"

import { makeStyles } from "@material-ui/core/styles"

const useStyles = makeStyles({
  main: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    textAlign: "center",
    marginBottom: "75px",
    maxHeight: "fit-content",
  },
  card: {
    // maxHeight: '500px',
    height: "fit-content",
    margin: "5px",
  },
  leftCard: {
    marginRight: "8px",
  },
  top5: {
    flex: 1,
    textAlign: "center",
    fontSize: "75%",
    padding: 0,
  },
  cardTitle: {
    margin: "0",
    textDecoration: "underline",
    fontWeight: "bold",
    fontSize: "80%",
    padding: "2px",
  },
  top5List: {
    padding: "1px",
    margin: 1,
  },
  cardContent: {
    margin: 0,
    fontSize: "11px",
    lineHeight: "13px",
    position: "relative",
    width: "100%",
    paddingBottom: 1,
  },
  backgroundColor: {
    backgroundColor: "white",
    border: "solid grey 1px",
  },
  red: {
    color: "#d60000",
  },
  green: {
    color: "#006400",
  },
  blue: {
    color: "darkblue",
  },
  topSources: {
    flex: "1 100px",
  },
  topWords: {
    flex: "1 100px",
  },
  colorComparison: {
    flex: "1 1 300px",
    maxHeight: "275px",
  },
  segmentBarChart: {
    flex: "1 1 400px",
    height: "fit-content",
  },
  chartholder: {
    position: "relative",
    width: "100%",
    height: "100%",
    padding: "5px",
    marginBottom: "50px",
  },
  bookChart: {
    height: "500px",
  },
  segChart: {
    height: "400px",
  },
  piechart: {
    height: "300px",
  },
  introSubtitle: {
    color: "rgb(83, 152, 255)",
    fontWeight: "bold",
  },
})

const getVersesInBook = book => {
  const objs = Object.values(OTNTRefData)
  const verses = objs.filter(x => x.book === book)
  return verses
}

// ---------------------- //
// PIE CHART DATA
// ---------------------- //

const percentageOfVerses = book => {
  const verses = getVersesInBook(book)
  const totalVerses = bibleBookList[book].verseCount
  const quotations = verses.filter(x => x.quotations.length > 0).length
  const allusions = verses.length - quotations
  return {
    verses: totalVerses,
    original: totalVerses - quotations - allusions,
    quotes: quotations,
    allusions: allusions,
  }
}

const createPieChartData = (book, {translations, language}) => {

  return {
    labels: [
      getTranslation(
        translations,

        language,
        "UI",
        "otnt_quotations"
      ),
      getTranslation(
        translations,

        language,
        "UI",
        "otnt_allusions"
      ),
      getTranslation(
        translations,

        language,
        "UI",
        "otnt_remainder"
      ),
    ],
    datasets: [
      {
        data: [
          percentageOfVerses(book).quotes,
          percentageOfVerses(book).allusions,
          percentageOfVerses(book).original,
        ],
        backgroundColor: ["rgb(209, 0, 209)", "rgb(255, 238, 4)", "lightgrey"],
        hoverBackgroundColor: ["purple", "rgb(184, 171, 0)", "grey"],
      },
    ],
  }
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      left: 10,
      right: 10,
      top: 0,
      bottom: 70,
    },
  },
  legend: {
    position: "top",
    labels: {
      fontSize: 9,
      boxWidth: 40,
    },
    // display: false
  },
}

// ---------------------- //
// BOOK BAR CHART DATA
// ---------------------- //

const BookBarChartOptions = {
  scales: {
    xAxes: [
      {
        stacked: true,
        ticks: {
          min: 0,
          stepSize: 1,
        },
      },
    ],
    yAxes: [{ stacked: true }],
  },
  maintainAspectRatio: false,
  legend: {
    position: "right",
    labels: {
      fontSize: 9,
      boxWidth: 20,
    },
    display: false,
  },
}

const createBookBarDataObj = (book, {translations, language}) => {
  const currentBookVerses = getVersesInBook(book)

  let quotations = []
  let allusions = []

  currentBookVerses.forEach(verse => {
    if (quotations.length < 1 && verse.quotations.length > 0) {
      quotations = [verse.quotations]
    } else if (verse.quotations.length > 0) {
      quotations = [...quotations, ...verse.quotations]
    }
    if (allusions.length < 1 && verse.allusions.length > 0) {
      allusions = [verse.allusions]
    } else if (verse.allusions.length > 0) {
      allusions = [...allusions, ...verse.allusions]
    }
  })

  let refObj = {}

  const allVerses = Object.values(OTNTRefData)
  quotations.forEach(quotation => {
    let string = ""
    if (typeof quotation === "object") {
      string = quotation.join()
    } else if (typeof quotation === "string") {
      string = quotation
    }
    const quantity = allVerses.filter(x => x.originalRef.indexOf(string) !== -1)
    quantity.forEach(ref => {
      if (!refObj[ref.book]) {
        refObj[ref.book] = {
          quotations: [ref.fullRef],
          allusions: [],
        }
      } else {
        if (refObj[ref.book].quotations.indexOf(ref.fullRef) === -1) {
          refObj[ref.book].quotations.push(ref.fullRef)
        }
      }
    })
  })

  allusions.forEach(allusion => {
    let string = ""
    if (typeof allusion === "object") {
      string = allusion.join()
    } else if (typeof allusion === "string") {
      string = allusion
    }
    const quantity = allVerses.filter(x => x.originalRef.indexOf(string) !== -1)
    quantity.forEach(ref => {
      if (!refObj[ref.book]) {
        refObj[ref.book] = {
          quotations: [],
          allusions: [ref.fullRef],
        }
      } else {
        if (refObj[ref.book].allusions.indexOf(ref.fullRef) === -1) {
          refObj[ref.book].allusions.push(ref.fullRef)
        }
      }
    })
  })

  const booksInOrder = Object.keys(bibleBookList)

  let bookData = {
    labels: [],
    quotes: [],
    allusions: [],
  }

  booksInOrder.forEach(book => {
    if (refObj[book]) {
      bookData.labels.push(
        getTranslation(
          translations,

          language,
          "Books",
          `abbrev_${book}`
        )
      )
      bookData.quotes.push(refObj[book].quotations.length)
      bookData.allusions.push(refObj[book].allusions.length)
    }
  })

  return {
    labels: bookData.labels,
    datasets: [
      {
        label: getTranslation(
          translations,

          language,
          "UI",
          "otnt_quotations"
        ),
        data: bookData.quotes,
        backgroundColor: "rgb(209, 0, 209)",
        borderColor: "purple",
        borderWidth: 1,
        hoverBackgroundColor: "purple",
        hoverBorderColor: "purple",
      },
      {
        label: getTranslation(
          translations,

          language,
          "UI",
          "otnt_allusions"
        ),
        data: bookData.allusions,
        backgroundColor: "rgb(255, 238, 4)",
        borderColor: "rgb(184, 171, 0)",
        borderWidth: 1,
        hoverBackgroundColor: "rgb(184, 171, 0)",
        hoverBorderColor: "rgb(184, 171, 0)",
      },
    ],
  }
}

// ---------------------- //
// SEGMENT BAR CHART DATA
// ---------------------- //

const SegmentBarChartOptions = {
  scales: {
    xAxes: [
      {
        stacked: true,
        ticks: {
          min: 0,
          stepSize: 1,
        },
      },
    ],
    yAxes: [
      {
        stacked: true,
        afterFit: function(scaleInstance) {
          scaleInstance.width = 90 // sets the width to 100px
        },
      },
    ],
  },
  maintainAspectRatio: false,
  legend: {
    position: "right",
    labels: {
      fontSize: 9,
      boxWidth: 40,
    },
    display: false,
  },
}

const createSegmentBarDataObj = (book, {translations, language, version, AvailableContent}) => {
  const segmentIDs = Object.keys(SegmentRefs)
  const verses = getVersesInBook(book)

  const versificationType = AvailableContent
    ? AvailableContent.content[language].versions[version]
        .versification
    : null

  const SegmentQueriesArray =
    versificationType === "standard"
      ? require("../../../data/SegmentQueriesStandard.json")
      : require("../../../data/SegmentQueriesArray.json")
  let barData = {
    quotes: [],
    allusions: [],
    original: [],
    labels: [],
  }

  segmentIDs.forEach(segment => {
    const isIncluded =
      SegmentRefs[segment].Segment.substring(0, 1) === "S"
        ? SegmentRefs[segment].book[0] === book ||
          SegmentRefs[segment].book[1] === book
        : false
    if (SegmentRefs[segment].Segment.substring(0, 1) === "S" && isIncluded) {
      const segmentData = SegmentQueriesArray.filter(x => x.segment === segment)
      const modifiedSegData = {
        starting_chapter: parseInt(segmentData[0].starting_chapter),
        ending_chapter: parseInt(segmentData[0].ending_chapter),
        starting_verse: parseInt(segmentData[0].starting_verse),
        ending_verse: parseInt(segmentData[0].ending_verse),
      }
      const chapterDiff =
        modifiedSegData.ending_chapter - modifiedSegData.starting_chapter

      const segVerses = verses.filter(verse => {
        verse.chapter = parseInt(verse.chapter)
        verse.verse = parseInt(verse.verse)

        // if it's S096
        if (segment === "S096" && book === "1Sa") {
          if (verse.chapter === 31) {
            return true
          }
        } else if (segment === "S096" && book === "2Sa") {
          if (verse.chapter === 1) {
            return true
          } else if (verse.chapter === 2 && verse.verse <= 7) {
            return true
          }
        }
        // if it's S115
        else if (segment === "S115" && book === "1Ki") {
          if (verse.chapter === 22 && verse.verse >= 41) {
            return true
          }
        } else if (segment === "S115" && book === "2Ki") {
          if (verse.chapter === 1 || verse.chapter === 2) {
            return true
          } else if (verse.chapter === 3 && verse.verse <= 27) {
            return true
          }
        }

        // if it's a single chapter segment
        else if (
          chapterDiff === 0 &&
          verse.chapter === modifiedSegData.starting_chapter
        ) {
          if (
            verse.verse >= modifiedSegData.starting_verse &&
            verse.verse <= modifiedSegData.ending_verse
          ) {
            return true
          }
        }

        // if it's a 2 chapter segment
        else if (
          chapterDiff === 1 &&
          verse.chapter >= modifiedSegData.starting_chapter &&
          verse.chapter <= modifiedSegData.ending_chapter
        ) {
          if (
            verse.chapter === modifiedSegData.starting_chapter &&
            verse.verse >= modifiedSegData.starting_verse
          ) {
            return true
          } else if (
            verse.chapter === modifiedSegData.ending_chapter &&
            verse.verse <= modifiedSegData.ending_verse
          ) {
            return true
          }
        }

        // if it's a many chapter segment
        else if (
          chapterDiff > 1 &&
          verse.chapter >= modifiedSegData.starting_chapter &&
          verse.chapter <= modifiedSegData.ending_chapter
        ) {
          if (
            verse.chapter === modifiedSegData.starting_chapter &&
            verse.verse >= modifiedSegData.starting_verse
          ) {
            return true
          } else if (
            verse.chapter === modifiedSegData.ending_chapter &&
            verse.verse <= modifiedSegData.ending_verse
          ) {
            return true
          } else if (
            verse.chapter > modifiedSegData.starting_chapter &&
            verse.chapter < modifiedSegData.ending_chapter
          ) {
            return true
          }
        } else {
          return null
        }
        return null
      })

      if (segment === "S096" && book === "1Sa") {
        barData.labels.push("31:1-13")
        const quotations = segVerses.filter(x => x.quotations.length > 0).length
        barData.quotes.push(quotations)
        barData.allusions.push(segVerses.length - quotations)
        barData.original.push(
          segmentData[0].verseCount -
            quotations -
            (segVerses.length - quotations)
        )
      } else if (segment === "S096" && book === "2Sa") {
        barData.labels.push("1:1-2:7")
        const quotations = segVerses.filter(x => x.quotations.length > 0).length
        barData.quotes.push(quotations)
        barData.allusions.push(segVerses.length - quotations)
        barData.original.push(
          segmentData[0].verseCount -
            quotations -
            (segVerses.length - quotations)
        )
      } else if (segment === "S115" && book === "1Ki") {
        barData.labels.push("22:41-53")
        const quotations = segVerses.filter(x => x.quotations.length > 0).length
        barData.quotes.push(quotations)
        barData.allusions.push(segVerses.length - quotations)
        barData.original.push(
          segmentData[0].verseCount -
            quotations -
            (segVerses.length - quotations)
        )
      } else if (segment === "S115" && book === "2Ki") {
        barData.labels.push("1:1-3:27")
        const quotations = segVerses.filter(x => x.quotations.length > 0).length
        barData.quotes.push(quotations)
        barData.allusions.push(segVerses.length - quotations)
        barData.original.push(
          segmentData[0].verseCount -
            quotations -
            (segVerses.length - quotations)
        )
      } else if (segment !== "S096" || segment !== "S115") {
        barData.labels.push(
          versificationType === "standard" && SegmentRefs[segment].standardRef
            ? SegmentRefs[segment].standardRef
            : SegmentRefs[segment].ref
        )
        const quotations = segVerses.filter(x => x.quotations.length > 0).length
        barData.quotes.push(quotations)
        barData.allusions.push(segVerses.length - quotations)
        barData.original.push(
          segmentData[0].verseCount -
            quotations -
            (segVerses.length - quotations)
        )
      }
    }
  })

  return {
    labels: barData.labels,
    datasets: [
      {
        label: getTranslation(
          translations,

          language,
          "UI",
          "otnt_quotations"
        ),
        data: barData.quotes,
        backgroundColor: "rgb(209, 0, 209)",
        borderColor: "purple",
        borderWidth: 1,
        hoverBackgroundColor: "purple",
        hoverBorderColor: "purple",
      },
      {
        label: getTranslation(
          translations,

          language,
          "UI",
          "otnt_allusions"
        ),
        data: barData.allusions,
        backgroundColor: "rgb(255, 238, 4)",
        borderColor: "rgb(184, 171, 0)",
        borderWidth: 1,
        hoverBackgroundColor: "rgb(184, 171, 0)",
        hoverBorderColor: "rgb(184, 171, 0)",
      },
      {
        label: getTranslation(
          translations,

          language,
          "UI",
          "otnt_remainder"
        ),
        data: barData.original,
        backgroundColor: "lightgrey",
        borderColor: "grey",
        borderWidth: 1,
        hoverBackgroundColor: "grey",
        hoverBorderColor: "grey",
      },
    ],
  }
}

const SegmentData = props => {
  const translations = useStore.translations()[0]
  const AvailableContent = useStore.AvailableContent()[0]
  const language = useStore.language()[0]
  const version = useStore.version()[0]
  const [history, setHistory] = useStore.history()
  const [searchID, setSearchID] = useStore.searchID()
  const [currentSegmentID, setCurrentSegmentID] = useStore.currentSegmentID()
  const classes = useStyles()
  getVersesInBook(props.book)
  return (
    <div className={classes.root}>
      <p className={classes.introSubtitle}>
        {getTranslation(
          translations,
          language,
          "UI",
          "otnt_segment_data_title"
        )}
      </p>
      <div className={classes.main}>
        <Card
          className={`${classes.backgroundColor} ${classes.card} ${
            classes.colorComparison
          }`}
        >
          <CardHeader
            title={getTranslation(
              translations,
              language,
              "UI",
              "otnt_intro_piechart_title"
            )}
            disableTypography={true}
            className={classes.cardTitle}
          />
          <CardContent className={`${classes.cardContent} ${classes.piechart}`}>
            <div className={classes.chartholder}>
              <Doughnut
                data={createPieChartData(props.book, {
                  translations,
                  language,
                })}
                options={chartOptions}
                height={null}
                width={null}
              />
            </div>
          </CardContent>
        </Card>
        <Card
          className={`${classes.top5} ${classes.leftCard} ${
            classes.backgroundColor
          } ${classes.card} ${classes.segmentBarChart}`}
        >
          <CardHeader
            title={getTranslation(
              translations,
              language,
              "UI",
              "otnt_intro_segment_barchart_title"
            )}
            disableTypography={true}
            className={classes.cardTitle}
          />
          <CardContent
            className={`${classes.cardContent} ${classes.segChart}`}
            style={{
              height: `${createSegmentBarDataObj(props.book, {
                translations,
                language,
                version,
                AvailableContent,
              }).labels.length *
                15 +
                100}px`,
            }}
          >
            <div className={classes.chartholder}>
              <HorizontalBar
                data={createSegmentBarDataObj(props.book, {
                  translations,
                  language,
                  version,
                  AvailableContent,
                })}
                width={null}
                height={null}
                options={SegmentBarChartOptions}
              />
            </div>
          </CardContent>
        </Card>
        <Card
          className={`${classes.top5} ${classes.leftCard} ${
            classes.backgroundColor
          } ${classes.card}  ${classes.segmentBarChart}`}
        >
          <CardHeader
            title={getTranslation(
              translations,
              language,
              "UI",
              "otnt_intro_book_barchart_title"
            )}
            disableTypography={true}
            className={classes.cardTitle}
          />
          <CardContent
            className={`${classes.cardContent} ${classes.bookChart}`}
            style={{
              height: `${createBookBarDataObj(props.book, {
                translations,
                language,
              }).labels.length *
                15 +
                100}px`,
            }}
          >
            <div className={classes.chartholder}>
              <HorizontalBar
                data={createBookBarDataObj(props.book, {
                  translations,
                  language,
                })}
                width={null}
                height={null}
                options={BookBarChartOptions}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default SegmentData
